import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { 
  Award, Plus, Trash2, Loader2, Edit, Search,
  Upload, Check, X, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { PresignedImage } from '../components/PresignedMedia';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBadges() {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editBadge, setEditBadge] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    criteria_type: 'signup_order',
    criteria_value: 100,
    icon_url: ''
  });

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const response = await axios.get(`${API}/admin/badges`);
      setBadges(response.data.badges || []);
    } catch (error) {
      console.error('Failed to fetch badges:', error);
      toast.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Recommend PNG with transparency
    if (file.type !== 'image/png') {
      toast.info('Tip: PNG files with transparency work best for badges');
    }

    setUploadingIcon(true);
    try {
      // Get presigned upload URL
      const urlResponse = await axios.post(`${API}/admin/badges/upload-url`, null, {
        params: {
          filename: file.name,
          content_type: file.type
        }
      });

      const { upload_url, file_path } = urlResponse.data;

      // Upload to R2
      await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });

      setFormData(prev => ({ ...prev, icon_url: file_path }));
      toast.success('Icon uploaded!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload icon');
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Badge name is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editBadge) {
        await axios.put(`${API}/admin/badges/${editBadge.id}`, formData);
        toast.success('Badge updated!');
      } else {
        await axios.post(`${API}/admin/badges`, formData);
        toast.success('Badge created!');
      }
      
      setModalOpen(false);
      resetForm();
      fetchBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save badge');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (badge) => {
    if (!confirm(`Delete badge "${badge.name}"? This will remove it from all users.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/badges/${badge.id}`);
      toast.success('Badge deleted');
      fetchBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete badge');
    }
  };

  const handleToggleActive = async (badge) => {
    try {
      await axios.put(`${API}/admin/badges/${badge.id}`, {
        active: !badge.active
      });
      toast.success(badge.active ? 'Badge disabled' : 'Badge enabled');
      fetchBadges();
    } catch (error) {
      toast.error('Failed to update badge');
    }
  };

  const openEditModal = (badge) => {
    setEditBadge(badge);
    setFormData({
      name: badge.name,
      description: badge.description,
      criteria_type: badge.criteria_type,
      criteria_value: badge.criteria_value,
      icon_url: badge.icon_url || ''
    });
    setModalOpen(true);
  };

  const resetForm = () => {
    setEditBadge(null);
    setFormData({
      name: '',
      description: '',
      criteria_type: 'signup_order',
      criteria_value: 100,
      icon_url: ''
    });
  };

  const getCriteriaLabel = (type, value) => {
    switch (type) {
      case 'signup_order':
        return `First ${value} users`;
      case 'sidequest_count':
        return `Complete ${value} sidequests`;
      case 'manual':
        return 'Manually awarded';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Badges</h1>
          <p className="text-neutral-400">Manage achievement badges for users</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setModalOpen(true); }}
          className="bg-red-500 hover:bg-red-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Badge
        </Button>
      </div>

      {/* Badges Grid */}
      {badges.length === 0 ? (
        <div className="text-center py-12 bg-[#121212] rounded-2xl border border-white/10">
          <Award className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <p className="text-neutral-400">No badges created yet</p>
          <p className="text-neutral-500 text-sm mt-1">Create badges to reward users for achievements</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {badges.map(badge => (
            <div 
              key={badge.id}
              className={`bg-[#121212] rounded-2xl border p-5 ${
                badge.active ? 'border-white/10' : 'border-red-500/30 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {badge.icon_url ? (
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center overflow-hidden">
                      <PresignedImage 
                        src={badge.icon_url}
                        alt={badge.name}
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-white">{badge.name}</h3>
                    {!badge.active && (
                      <Badge variant="outline" className="text-xs border-red-500/30 text-red-400">
                        Disabled
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => openEditModal(badge)}
                    className="text-neutral-400 hover:text-white"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDelete(badge)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <p className="text-neutral-400 text-sm mb-3">{badge.description}</p>
              
              <div className="flex items-center justify-between">
                <Badge className="bg-white/5 text-neutral-300 border-white/10">
                  {getCriteriaLabel(badge.criteria_type, badge.criteria_value)}
                </Badge>
                <button
                  onClick={() => handleToggleActive(badge)}
                  className={`text-xs px-2 py-1 rounded ${
                    badge.active 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {badge.active ? 'Active' : 'Enable'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editBadge ? 'Edit Badge' : 'Create Badge'}
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              {editBadge ? 'Update badge details' : 'Create a new achievement badge'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Icon Upload */}
            <div>
              <Label className="text-white">Badge Icon</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center overflow-hidden border-2 border-dashed border-white/20">
                  {formData.icon_url ? (
                    <PresignedImage 
                      src={formData.icon_url}
                      alt="Badge icon"
                      className="w-14 h-14 object-contain"
                    />
                  ) : (
                    <Award className="w-8 h-8 text-neutral-500" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleIconUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingIcon}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    {uploadingIcon ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload Icon
                  </Button>
                  <p className="text-xs text-neutral-500 mt-1">
                    PNG with transparency recommended
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="name" className="text-white">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Early Adopter"
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-white">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Awarded to the first 100 users"
                className="bg-white/5 border-white/10 text-white mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="criteria_type" className="text-white">Criteria Type</Label>
              <Select
                value={formData.criteria_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, criteria_type: value }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  <SelectItem value="signup_order">Signup Order (First X users)</SelectItem>
                  <SelectItem value="sidequest_count">Sidequest Count (Complete X)</SelectItem>
                  <SelectItem value="manual">Manual (Admin awarded)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.criteria_type !== 'manual' && (
              <div>
                <Label htmlFor="criteria_value" className="text-white">
                  {formData.criteria_type === 'signup_order' ? 'First X Users' : 'Required Count'}
                </Label>
                <Input
                  id="criteria_value"
                  type="number"
                  min="1"
                  value={formData.criteria_value}
                  onChange={(e) => setFormData(prev => ({ ...prev, criteria_value: parseInt(e.target.value) || 1 }))}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            )}

            <DialogFooter>
              <Button 
                type="button"
                variant="ghost" 
                onClick={() => setModalOpen(false)}
                className="text-neutral-400"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={submitting}
                className="bg-red-500 hover:bg-red-600"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {editBadge ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
